import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import type { OutletInfo } from '@photobooth/api-client';
import type { Outlet } from '@photobooth/types';

interface OutletAccessManagerProps {
  userId: string;
  availableOutlets: Outlet[];
}

export function OutletAccessManager({ userId, availableOutlets }: OutletAccessManagerProps) {
  const [assignedOutlets, setAssignedOutlets] = useState<OutletInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isAddingOutlet, setIsAddingOutlet] = useState(false);
  const [selectedOutletId, setSelectedOutletId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchAssignedOutlets();
  }, [userId]);

  const fetchAssignedOutlets = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await api.cashiers.getOutlets(userId);
      if (response.success && response.data) {
        const raw = response.data as any;
        // Support both direct array and wrapped { data: OutletInfo[] } shapes
        const outletsData = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : [];
        setAssignedOutlets(outletsData);
      } else {
        setError(response.error?.message || 'Failed to load outlet access');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load outlet access');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddOutlet = async () => {
    if (!selectedOutletId) {
      setError('Please select an outlet');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.cashiers.manageOutletAccess(userId, {
        outletId: selectedOutletId,
        action: 'add',
      });

      if (response.success) {
        setSuccessMessage('Outlet access added successfully');
        setIsAddingOutlet(false);
        setSelectedOutletId('');
        await fetchAssignedOutlets();
      } else {
        setError(response.error?.message || 'Failed to add outlet access');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add outlet access');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveOutlet = async (outletId: string) => {
    if (!confirm('Are you sure you want to remove access to this outlet?')) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await api.cashiers.manageOutletAccess(userId, {
        outletId,
        action: 'remove',
      });

      if (response.success) {
        setSuccessMessage('Outlet access removed successfully');
        await fetchAssignedOutlets();
      } else {
        setError(response.error?.message || 'Failed to remove outlet access');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove outlet access');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get outlets that are not yet assigned
  const unassignedOutlets = availableOutlets.filter(
    (outlet) => !assignedOutlets.some((assigned) => assigned.outletId === outlet.outletId)
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text">Outlet Access</h2>
        {!isAddingOutlet && unassignedOutlets.length > 0 && (
          <button
            onClick={() => setIsAddingOutlet(true)}
            disabled={isSubmitting}
            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
          >
            Add Outlet
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded">
          {successMessage}
        </div>
      )}

      {isAddingOutlet && (
        <div className="border border-gray-300 rounded-md p-4 bg-gray-50">
          <h3 className="text-sm font-medium text-text mb-3">Add Outlet Access</h3>
          <div className="space-y-3">
            <div>
              <label htmlFor="outlet-select" className="block text-sm font-medium text-text mb-1">
                Select Outlet
              </label>
              <select
                id="outlet-select"
                value={selectedOutletId}
                onChange={(e) => setSelectedOutletId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">-- Select an outlet --</option>
                {unassignedOutlets.map((outlet) => (
                  <option key={outlet.outletId} value={outlet.outletId}>
                    {outlet.name} ({outlet.location})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddOutlet}
                disabled={isSubmitting || !selectedOutletId}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
              >
                {isSubmitting ? 'Adding...' : 'Add'}
              </button>
              <button
                onClick={() => {
                  setIsAddingOutlet(false);
                  setSelectedOutletId('');
                  setError('');
                }}
                disabled={isSubmitting}
                className="bg-gray-200 text-text px-4 py-2 rounded-md hover:bg-gray-300 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-gray-300 rounded-md">
        {assignedOutlets.length === 0 ? (
          <div className="p-4 text-center text-text-muted">
            No outlets assigned. Click "Add Outlet" to grant access.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {assignedOutlets.map((outlet) => (
              <div
                key={outlet.outletId}
                className="p-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div>
                  <div className="text-sm font-medium text-text">{outlet.name}</div>
                  <div className="text-sm text-text-muted">{outlet.location}</div>
                </div>
                <button
                  onClick={() => handleRemoveOutlet(outlet.outletId)}
                  disabled={isSubmitting}
                  className="text-red-600 hover:text-red-800 disabled:opacity-50 text-sm font-medium"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {unassignedOutlets.length === 0 && assignedOutlets.length > 0 && (
        <p className="text-sm text-text-muted">
          All available outlets have been assigned to this cashier.
        </p>
      )}
    </div>
  );
}
